import { Injectable } from '@angular/core';
import { Sample } from 'src/app/shared/models/sample.model';
import AnalysesDetailService from 'src/app/shared/analyses-detail/analyses-detail.service';
import { Analysis, AnalysisType, QuotedItem, TestQuotationLine } from 'projects/dop-ordering/src/public-api';
import { CurrentOrder } from 'src/app/shared/models/current-order.model';
import { flatMap, chain } from 'lodash';
import { OrderDataService } from 'src/app/shared/data-service/order-data.service';
import { GetScInvoiceableItemType, SampleInvoiceableItems } from 'src/app/shared/models/sample-invoiceable-items.model';
import { LinkedSpecification } from 'src/app/shared/models/linked-specification.model';
import { LinkedInvoiceableItem } from 'src/app/shared/models/linked-invoiceable-item.model';
import { SpecificationHelper } from './specification-helper.service';
import { SpecificationResponse } from 'src/app/shared/models/specification/specification-response.model';
import { OrderCommandService } from 'src/app/shared/data-service/order-command.service';
import { SpecificationType } from 'src/app/shared/models/specification/specification-type.model';
import { SpecificationViewModel } from 'src/app/shared/models/specification/specification-view-model.model';
import { ParametersService } from './parameters.service';
import { Unit } from 'src/app/shared/models/specification/units.model';
import { FavoriteAnalysisService } from '../add-analyses/favorite-analyses.service';
import { InvoiceableItems } from 'src/app/shared/models/order-creation/favorite-invoicable-item.model';
import { forkJoin } from 'rxjs';
import { LegacyMatrixService } from 'src/app/shared/services/legacy-matrix.service';
import { QuotedItemApplicabilityRule } from 'src/app/shared/order-validator/rules/quoteditems-applicability.rule';
import { OrderDetailsCleanUpService } from 'src/app/shared/order-cleanup/order-details-cleanup.service';
import { SettingsService } from 'src/app/shared/data-service/settings.service';
import { SettingsKey } from 'src/app/core/store/settings-config.keys';
import { MonographService } from '../monograph/monograph.service';
import { MonographSalesCatalogue } from 'src/app/shared/models/monograph/monograph-sales-catalogue.model';
import { SampleAnalysisItemsType } from 'src/app/shared/models/monograph/sample-analysis-items-type.model';
import { CommercialItemValidationResponse } from 'src/app/shared/models/commercial-item-validation-response.model';
import { SalesCatalogueAccountRule } from 'src/app/shared/order-validator/rules/salescatalogue-account.rule';
import { SalesCatalogueCommercialItemRule } from 'src/app/shared/order-validator/rules/salescatalogue-commercial-item.rule';
import { EolctDataService } from 'src/app/shared/data-service/eolct-data.service';
import { MonographValidationResponse } from 'src/app/shared/models/monograph/monograph-validation-response.model';
import { ProductApplicabilityScRequest } from 'src/app/shared/models/legacy-matrix-applicability/product-applicability-sc-request.model';
import { SalesCatalogueApplicability } from 'src/app/shared/models/legacy-matrix-applicability/sales-catalogue-applicability.model';
import { SampleResponse } from 'src/app/shared/models/sample-response.model';
import { _fixedSizeVirtualScrollStrategyFactory } from '@angular/cdk/scrolling';
import { OrderState } from 'src/app/shared/enums/order-state.enum';
import { CurrentOrderService } from 'src/app/shared/services/current-order.service';
import { ReportGrouping } from 'src/app/shared/enums/report-grouping.enum';
import { UsePriority } from 'src/app/shared/enums/use-priority.enum';
import { SharedMessageService } from 'src/app/shared/services/shared-message.service';

@Injectable({
    providedIn: 'root'
})
export class LinkedAnalysisSpecificationService {

    currentOrder: CurrentOrder;
    quotedItemApplicabilityRule: QuotedItemApplicabilityRule;
    deletedPackages: Analysis[] = [];
    salesCatalogueAccountRule: SalesCatalogueAccountRule;
    salesCatalogueCommercialItemRule: SalesCatalogueCommercialItemRule;
    deletedScPackages: any[] = [];

    constructor(
        private orderDataService: OrderDataService,
        private specificationHelper: SpecificationHelper,
        private orderCommandService: OrderCommandService,
        private parametersService: ParametersService,
        private favoriteAnalysisService: FavoriteAnalysisService,
        private legacyMatrixService: LegacyMatrixService,
        private orderDetailsCleanUpService: OrderDetailsCleanUpService,
        private settingsService: SettingsService,
        private monographService: MonographService,
        private eolctQueryService: EolctDataService,
        private currentOrderService: CurrentOrderService,
        private sharedMessageService : SharedMessageService) {
    }

    getInvoiceableItems(codes: string[]) {
        return this.orderDataService.getSampleInvoiceableItems(codes);
    }

    async mapPOInvoiceableItems(
        currentOrder: CurrentOrder,
        invoiceableItems: LinkedInvoiceableItem[],
        isPendingOrder = true,
        isFavOrPrevSample = false) {
        // arrange data
        this.currentOrder = currentOrder;
        const testCodes = flatMap(invoiceableItems.map(x => x.sampleInvoiceableItemList
            .filter(t => !t.monographCode).map(y => y.testCode)));
        const data = await forkJoin({
            testParamResp: this.orderDataService.getTestParameters(testCodes),
            favAnalysis: this.getFavAnalysis()
        }).toPromise();


        const testParamResp = data.testParamResp;
        const favAnalysis: InvoiceableItems = data.favAnalysis;

        // map objects
        await this.mapAnalysis(invoiceableItems, isPendingOrder, favAnalysis, isFavOrPrevSample);
        await this.mapSpecifications(testParamResp, invoiceableItems);
    }

    async reMapSpecifications(currentOrder: CurrentOrder, invoiceableItems: LinkedInvoiceableItem[]) {
        this.currentOrder = currentOrder;
        const testCodes = flatMap(invoiceableItems
            .map(x => x.sampleInvoiceableItemList.filter(t => !t.monographCode).map(y => y.testCode)));
        const testParamResp = await this.orderDataService.getTestParameters(testCodes).toPromise();
        await this.mapSpecifications(testParamResp, invoiceableItems);
    }

    async getFavAnalysis(): Promise<InvoiceableItems> {
        return new Promise(async (resolve, reject) => {
            this.favoriteAnalysisService.getFavAnalysis(this.currentOrder.quotation.code).subscribe(data => {
                resolve(data);
            }, error => {
                resolve(new InvoiceableItems());
            });
        });
    }

    async mapAnalysis(
        invoiceableItems: LinkedInvoiceableItem[],
        isPendingOrder = true,
        favAnalysis: InvoiceableItems,
        isFavOrPrevSample = false) {
        let isLoaded = false;
        const planningLineIds: number[] = [];
        let salesCatalogues: any;
        for (const sample of this.currentOrder.samples) {
            sample.analysisItemsOfType = SampleAnalysisItemsType.All;
            this.deletedPackages = [];
            this.deletedScPackages = [];

            if (isFavOrPrevSample) {
                const newSample = this.currentOrder.newsamples?.filter(x => x.sampleCode.toString() === sample.sampleCode).shift();
                if (newSample) {
                    await this.loadQuotedItemRule(sample);
                    if (!isLoaded) {
                        salesCatalogues = await this.loadSalesCatalogueRule(invoiceableItems, sample.product?.productCode);
                        isLoaded = true;
                    }
                }
            } else {
                await this.loadQuotedItemRule(sample);

                if (!isLoaded) {
                    salesCatalogues = await this.loadSalesCatalogueRule(invoiceableItems, sample.product?.productCode);
                    isLoaded = true;
                }
            }
            this.initializeEmptyProperties(sample);
            const sampleInvoiceableItems = invoiceableItems
                .filter(x => x.sampleId.toString() === sample.sampleCode)[0]?.sampleInvoiceableItemList;

            this.assignPreparationtests(sampleInvoiceableItems, salesCatalogues);

            // map analysis only if there are any analysis applicable to matrix else clean up all
            if ((this.quotedItemApplicabilityRule && (this.quotedItemApplicabilityRule.quotationCommercialItems
                || this.quotedItemApplicabilityRule.monoGraphSalesCatalogItems))
                || (this.salesCatalogueCommercialItemRule?.salesCatalogueCommercialItems)) {
                if (sampleInvoiceableItems && sampleInvoiceableItems.length > 0) {
                    for (const iterator of sampleInvoiceableItems) {
                        await this.mapPlanningLines(sample, iterator, isPendingOrder,
                            favAnalysis, isFavOrPrevSample);

                        if (isFavOrPrevSample) {
                            if (!sample.planningLines.some(x => x.id === iterator.id)) {
                                planningLineIds.push(iterator.id);
                            }
                        }
                    }
                    sample.analyses = sample.analyses.sort((a, b) => a.sequence - b.sequence);
                    if (sample.analyticalReportGroupingRule ? sample.analyticalReportGroupingRule === ReportGrouping.SAMPLE  : this.currentOrder.orderTemplate.usePriority === UsePriority.SAMPLE)
                    {
                        sample.analyses = this.updateAnalysis(sample.analyses, sample.priorityLevel);
                    }                    
                    sample.planningLines = sample.planningLines.sort((a, b) => a.id - b.id);
                    this.mapAnalysisDisplay(sample);
                }
            } else {
                if (isPendingOrder) {
                    this.orderDetailsCleanUpService.deleteAllAnalyses([sample.sampleCode]);
                }
            }
        }
        if (this.currentOrder.orderTemplate.isSampleLevelMatrix
            && isFavOrPrevSample
            && planningLineIds?.length > 0) {
            this.orderDetailsCleanUpService.deletePlanningLines(planningLineIds);
        }
    }

    updateAnalysis(analyses: Analysis[],priorityValue: string): Analysis[] {
        return analyses?.map(analysis => {
          this.updateSurgeIcon(analysis, priorityValue);
          return analysis;
        });
    }

    updateSurgeIcon(analysis: { showSurgeIcon?: boolean, structuredComment?: any }, priorityValue: string): void {
        analysis.showSurgeIcon = !this.sharedMessageService.isPriorityMismatch(this.sharedMessageService.convertValue(priorityValue), analysis.structuredComment?.value || analysis.structuredComment);
      }
    


    private assignPreparationtests(sampleInvoiceableItems: SampleInvoiceableItems[], salesCatalogues: any) {
        sampleInvoiceableItems?.forEach(u => {
            const scMembers = salesCatalogues?.salesCatalogues?.filter((x: { code: string; }) => x.code === u.salesCatagloueCode).shift();
            const prepTest = scMembers?.members.filter((c: { code: string; }) => c.code === u.testCode).shift()?.preparationTests;

            if (prepTest && u.isMrt === false) {
                u.preparationTestCodes = prepTest;
            }

            this.currentOrder.quotedItems?.quotationCommercialItem?.freeQuotationLinesTest?.forEach(t => {
                if (t.code === u.quotationLineCode && t.testCode === u.testCode) {
                    const prep = t.preparationTestCodes;

                    if (prep && u.isMrt === false) {
                        u.preparationTestCodes = prep;
                    }
                }
            });
        });
    }

    private async loadQuotedItemRule(sample: Sample) {
        // Filter matrix applicable quoted Items
        let applicableQuotableItems: QuotedItem;
        if (this.currentOrder.orderTemplate?.isSampleLevelMatrix) {
            applicableQuotableItems = sample.product?.productCode ? await this.legacyMatrixService
                .filterMatrixApplicableQuotableItems(sample.product.productCode, this.currentOrder.quotedItems) : {};
        } else {
            applicableQuotableItems = this.currentOrder.quotedItems;
        }
        let monographResponse: MonographSalesCatalogue;
        const monographSalesCatalogCode = await this.settingsService.getSettingValue(SettingsKey.ConfigureMonograph);
        if (monographSalesCatalogCode && this.currentOrder.orderTemplate?.useMonograph) {
            monographResponse = await this.monographService.getMonographs(this.currentOrder.quotation?.code,
                monographSalesCatalogCode);
        }
        this.quotedItemApplicabilityRule = new QuotedItemApplicabilityRule(applicableQuotableItems?.quotationCommercialItem,
            monographResponse);
    }

    private async loadSalesCatalogueRule(invoiceableItems: LinkedInvoiceableItem[], matrixCode?: string): Promise<any> {
        let ciSalesCatalogueCodes: string[] = [];
        invoiceableItems.forEach(x => {
            x.sampleInvoiceableItemList.forEach(sampleInvoiceableItem => {
                if (sampleInvoiceableItem.salesCatagloueCode && !ciSalesCatalogueCodes.includes(sampleInvoiceableItem.salesCatagloueCode
                )) {
                    ciSalesCatalogueCodes.push(sampleInvoiceableItem.salesCatagloueCode);
                }
            });
        });

        ciSalesCatalogueCodes = [...new Set(ciSalesCatalogueCodes.map(obj => obj))];
        if (ciSalesCatalogueCodes && ciSalesCatalogueCodes.length > 0) {
            const scCodes = await this.getSalesCatalogueCode();
            if (scCodes.salesCatalogues && scCodes.salesCatalogues.length > 0) {
                this.salesCatalogueAccountRule = new SalesCatalogueAccountRule(scCodes);

                const response = this.salesCatalogueAccountRule.validateAccountLinkedSalesCatalogue(ciSalesCatalogueCodes);
                if (response && response.isValid) {
                    const scMembers = await this.getCommercialItems();
                    if (scMembers && scMembers.salesCatalogues && scMembers.salesCatalogues.length > 0) {
                        let scApplicability: SalesCatalogueApplicability[] = [];
                        const useMatrix = this.currentOrder.orderTemplate?.isSampleLevelMatrix;
                        if (useMatrix && matrixCode) {
                            scApplicability = await this.getCommercialItemsApplicability(matrixCode, response.data);
                        }
                        this.salesCatalogueCommercialItemRule = new SalesCatalogueCommercialItemRule(scMembers, useMatrix, scApplicability);
                        return scMembers;
                    }
                }
                return response.isValid;
            }

            return false;
        }

        return true;
    }

    getSalesCatalogueCode(): Promise<any> {
        return new Promise((resolve) => {
            this.orderDataService.getSalesCatalogsAndMemberDetails(this.currentOrder.quotation.salesPartnerCode, this.currentOrder.account.code,
                this.currentOrder.quotation.code).subscribe(data => {
                    resolve(data);
                }, error => {
                    console.log('Error from getSalesCatalogueCode:' + JSON.stringify(error));
                    resolve([]);
                });
        });
    }

    getCommercialItems(): Promise<any> {
        return new Promise((resolve) => {
            this.orderDataService.getSalesCatalogsAndMemberDetails(this.currentOrder.quotation.salesPartnerCode, this.currentOrder.account.code,
                this.currentOrder.quotation.code).subscribe(data => {
                    resolve(data);
                }, error => {
                    console.log('Error from getCommercialItems:' + JSON.stringify(error));
                    resolve({});
                });
        });
    }

    getCommercialItemsApplicability(matrixCode: string, scCodes: string[]): Promise<SalesCatalogueApplicability[]> {
        const request = new ProductApplicabilityScRequest();
        request.legacyMatrixCode = matrixCode;
        request.labSCode = this.currentOrder?.labDetails?.code;
        request.accountCode = this.currentOrder?.account?.code;
        return new Promise((resolve) => {
            this.orderDataService.getSalesCatalogProductApplicability(request).subscribe(data => {
                resolve(data.salesCatelogueApplicabilities);
            }, error => {
                console.log('Error from getSalesCatalogProductApplicability:' + JSON.stringify(error));
            });
        });
    }

    async mapSpecifications(testParamResp: any, invoiceableItems: LinkedInvoiceableItem[]) {
        const analyses: Analysis[] = flatMap(this.currentOrder.samples.map(x => x.analyses));
        const testDetails: Unit[] = await this.parametersService.getTestDetails(analyses, this.currentOrder);

        this.currentOrder.samples?.forEach(sample => {
            const sampleInvoiceableItems = invoiceableItems
                .filter(x => x.sampleId.toString() === sample.sampleCode)
            [0]?.sampleInvoiceableItemList?.filter(x => testDetails.some(z => z.code === x.testCode));
            if (sampleInvoiceableItems && sampleInvoiceableItems.length > 0) {
                this.mapTestParameters(sample, sampleInvoiceableItems, testParamResp, testDetails);
                this.mapSpecificationsViewModel(sample, invoiceableItems, this.currentOrder.orderState === OrderState.Pending);
                this.mapSpecificationDisplay(sample, sampleInvoiceableItems);
            }
        });
    }

    private initializeEmptyProperties(sample: Sample) {
        sample.specifications = sample?.specifications ?? [];
        sample.analyses = sample?.analyses ?? [];
        sample.planningLines = sample?.planningLines ?? [];
        sample.specificationResponses = sample?.specificationResponses ?? [];
    }

    private mapSpecificationDisplay(sample: Sample, sampleInvoiceableItems: SampleInvoiceableItems[]) {
        const mappedSpecifications: SpecificationResponse[] = flatMap(sampleInvoiceableItems.map(x => x.specifications.map(y => {
            return {
                specificationId: y.specificationCode,
                parameterCode: y.parameterCode,
                specificationTypeId: SpecificationType[y.specificationType],
            } as unknown as SpecificationResponse;
        })));

        if (mappedSpecifications) {
            sample.specificationResponses = [];
            sample.specificationResponses.push(...mappedSpecifications);
        }
    }

    private mapSpecificationsViewModel(sample: Sample, invoiceableItems: LinkedInvoiceableItem[], isPending: boolean) {
        sample.isSpecificationsAdded = true;
        const specificationMap = invoiceableItems.filter(x => x.sampleId === Number(sample.sampleCode)).shift()
            ?.sampleInvoiceableItemList?.map(a => a.specifications);
        const linkedSpecifications: LinkedSpecification[] = flatMap(specificationMap);
        const allSpecifications = this.specificationHelper
            .convertToSpecificationViewModel(sample, this.currentOrder.units, linkedSpecifications, isPending);
        sample.specifications = this.removeEmptySpecifications(allSpecifications);
    }

    removeEmptySpecifications(allSpecifications: SpecificationViewModel[]): SpecificationViewModel[] {
        const specifications: SpecificationViewModel[] = [];
        allSpecifications.forEach(specification => {
            if (!specification.testName) {
                // Its the case when the test added as PL is not longer available
                return;
            }
            const specificationToAdd: SpecificationViewModel = {
                testCode: specification.testCode,
                testName: specification.testName,
                parameters: []
            };
            specification.parameters.forEach(param => {
                if ((param.standardSpecification
                    && this.hasDenominatorOrDenominatorChanged(param.standardSpecification)) ||
                    (param.servingSpecification && this.hasDenominatorOrDenominatorChanged(param.servingSpecification, true)
                        || (param.standardSpecification.min || param.standardSpecification.max || param.standardSpecification.target) ||
                        param.servingSpecification
                        && (param.servingSpecification.min || param.servingSpecification.max || param.servingSpecification.target) ||
                        param.dailyValueSpecification
                        && (param.dailyValueSpecification.min || param.dailyValueSpecification.max
                            || param.dailyValueSpecification.target) ||
                        param.labelClaimSpecification
                        && (param.labelClaimSpecification.min || param.labelClaimSpecification.max
                            || param.labelClaimSpecification.target))) {
                    specificationToAdd.parameters.push(param);
                }
            });

            if (specification.parameters.length > 0) {
                specifications.push(specificationToAdd);
            }
        });

        return specifications;
    }

    reMapAnalysisDisplay(sample: Sample) {
        this.mapAnalysisDisplay(sample);
    }
    private hasDenominatorOrDenominatorChanged(specification: any, isServingSpecification: boolean = false): boolean {
        return (!isServingSpecification && (specification.denominatorCode && specification.defaultDenominator &&
            specification.defaultDenominator.code !== specification.denominatorCode.code)) ||
            (specification.numeratorCode && specification.defaultNumerator &&
                specification.defaultNumerator.code !== specification.numeratorCode.code);
    }
    private mapTestParameters(sample: Sample, sampleInvoiceableItems: SampleInvoiceableItems[], testParamResp: any, testDetails: Unit[]) {
        const data = testParamResp.filter((x: any) => sampleInvoiceableItems.map(y => y.testCode).indexOf(x.testCode) !== -1);
        sample.testParameters = [];
        const groupedParameters = chain(data)
            .groupBy((x: any) => x.testCode)
            .map((value: any, key: any) => ({ testCode: key, parameters: value }))
            .value();

        groupedParameters.forEach((paramGroup: any) => {
            const testParam = {
                testCode: paramGroup.testCode,
                testName: testDetails.filter(test => test.code === paramGroup.testCode).shift()?.name,
                parameters: paramGroup.parameters,
            };
            sample.testParameters.push(testParam);
        });
    }

    private mapAnalysisDisplay(sample: Sample) {
        this.currentOrder = this.currentOrder || this.currentOrderService.currentOrder;
        sample.analysisDisplay = AnalysesDetailService.setupDisplayAnalysis(sample.analyses?.filter(a => !a.isMandatory),
            this.currentOrder.quotedItems?.quotationCommercialItem?.commercialItemSubGroups, this.currentOrder.orderTemplate?.useMultipleDetermination,
            this.currentOrder.standardCommentTemplate?.standardComments);
        if (this.currentOrder.orderState != OrderState.Submitted && this.currentOrder.orderState != OrderState.FavoriteOrder){
            this.displayMixedPriorityMessage(sample);
        }        
    }

    private displayMixedPriorityMessage(sample: Sample) {
        if (sample.analysisDisplay.filter(analysis => analysis.showSurgeIcon).length > 1) {
            this.currentOrderService.currentOrder.showSurgeIcon = true;
        }
        else if (sample.analysisDisplay.filter(analysis => analysis.showSurgeIcon).length === 1) {
            sample.analysisDisplay.forEach(analysis => {
                if (analysis.showSurgeIcon && !this.currentOrderService.currentOrder.showSurgeIcon) {
                    let setAnalysisTooltip = true;
                    if (analysis.childDisplay.filter(child => child.showSurgeIcon).length > 1) {
                        setAnalysisTooltip = false;
                        this.currentOrderService.currentOrder.showSurgeIcon = true;
                    }
                    else if (analysis.childDisplay.filter(child => child.showSurgeIcon).length === 1) {
                        analysis.childDisplay.forEach(child => {
                            if (child.showSurgeIcon && !this.currentOrderService.currentOrder.showSurgeIcon) {
                                child.enableTooltip = true;
                                this.currentOrderService.currentOrder.showSurgeIcon = true;
                                setTimeout(() => {
                                    child.enableTooltip = false;
                                }, 5000);
                                setAnalysisTooltip = false;
                                return;
                            }
                        });
                    }

                    if (setAnalysisTooltip) {
                        analysis.enableTooltip = true;
                        this.currentOrderService.currentOrder.showSurgeIcon = true;
                        setTimeout(() => {
                            analysis.enableTooltip = false;
                        }, 5000);

                        return;
                    }
                }
            });
        }
    }

    private async mapPlanningLines(
        sample: Sample,
        sampleInvoiceableItem: SampleInvoiceableItems,
        isPendingOrder = true, favAnalysis: InvoiceableItems, isFavOrPrevSample: boolean) {
        let analysis: Analysis;
        if(sampleInvoiceableItem.executionNumber == 0 || sampleInvoiceableItem.executionNumber == null){
            sampleInvoiceableItem.executionNumber = 1;
        }
        if (sampleInvoiceableItem.salesCatagloueCode && sampleInvoiceableItem.salesCatagloueCode.trim() !== '' &&
            !sampleInvoiceableItem.monographCode && !sampleInvoiceableItem.compendiumCode) {
            analysis = await this.commercialItemToAnalysis(sampleInvoiceableItem);
        } else {
            analysis = await this.invoiceableItemToAnalysis(sample, sampleInvoiceableItem,
                isPendingOrder, favAnalysis, isFavOrPrevSample);
        }

        analysis.sequence = sampleInvoiceableItem.id;
        if (analysis.analysisType === AnalysisType.CustomerPackage
            || analysis.analysisType === AnalysisType.FreeQLPackage
            || analysis.analysisType === AnalysisType.FreeQLTest
            || analysis.analysisType === AnalysisType.FullSubgroup
            || analysis.analysisType === AnalysisType.MonographPackage
            || analysis.analysisType === AnalysisType.MonographTest
            || analysis.analysisType === AnalysisType.SalesCataloguePackage
            || analysis.analysisType === AnalysisType.SalesCatalogueTest
            || analysis.analysisType === AnalysisType.SalesCatalogueTestBundle
            || analysis.analysisType === AnalysisType.SubgroupWithPackage
            || analysis.analysisType === AnalysisType.SubgroupWithTest) {
            if (this.isUniqueAnalyses(analysis, sample.analyses)) {
                if (this.currentOrder.orderTemplate?.isSampleLevelMatrix) {
                    if (analysis.analysisType === AnalysisType.MonographPackage || analysis.analysisType === AnalysisType.MonographTest) {
                        sample.analysisItemsOfType = SampleAnalysisItemsType.OnlyMonograph;
                    } else {
                        sample.analysisItemsOfType = SampleAnalysisItemsType.OnlyQuotedItems;
                    }
                }
                sample.analyses.push(analysis);
            }
            sample.planningLines.push({
                id: sampleInvoiceableItem.id,
                testCode: sampleInvoiceableItem.testCode,
                packageCode: sampleInvoiceableItem.packageCode,
                groupCode: sampleInvoiceableItem.subGroupCode,
                quotationLineCode: sampleInvoiceableItem.quotationLineCode,
                structuredComment: sampleInvoiceableItem.comment,
                isMrt: sampleInvoiceableItem.isMrt,
                executionNumber: sampleInvoiceableItem.executionNumber == 0 ? 1 : sampleInvoiceableItem.executionNumber
            });
        }
    }

    private async invoiceableItemToAnalysis(
        sample: Sample,
        sampleInvoiceableItem: SampleInvoiceableItems,
        isPendingOrder = true, favAnalysis: InvoiceableItems,
        isFavOrPrevSample = false): Promise<Analysis> {
        const analysis = {} as Analysis;
        // array maintained for packages, so that delete analysis call is not made for each test under package
        const isCustomerPackage = sampleInvoiceableItem.customerPackageCode !== undefined;
        const isSubgroup = sampleInvoiceableItem.subGroupCode !== undefined;
        const isMonoGraphInvoiceableItem = sampleInvoiceableItem.monographCode !== undefined
            && sampleInvoiceableItem.salesCatagloueCode !== undefined;
        if (sampleInvoiceableItem.salesCatagloueCode && isFavOrPrevSample && !isMonoGraphInvoiceableItem) {
            const ciCode = sampleInvoiceableItem.testCode ?? sampleInvoiceableItem.packageCode;
            this.orderCommandService.removeSampleInvoiceableItemForSCTest(
                sample.sampleCode, sampleInvoiceableItem.salesCatagloueCode, ciCode).toPromise();
        }
        if (isCustomerPackage) {
            analysis.analysisType = AnalysisType.CustomerPackage;
            analysis.eurodatKey = sampleInvoiceableItem.customerPackageCode;
            analysis.executionNumber = sampleInvoiceableItem.executionNumber;
            analysis.subGroupKey = sampleInvoiceableItem.customerPackageCode;
            const customerPackage = this.currentOrder.quotedItems.quotationCommercialItem.customerPackages
                .filter(cp => cp.code === sampleInvoiceableItem.customerPackageCode)[0];
            if (customerPackage) {
                analysis.subgroupCode = customerPackage.comlimsCode;
                analysis.displayName = customerPackage.name;
                analysis.price = customerPackage.price;
                analysis.isCustomerPackage = true;
                analysis.sortIndex = customerPackage.sortIndex;
                analysis.structuredComment = sampleInvoiceableItem.comment;
            }
            return analysis;
        }

        if (isSubgroup) {
            analysis.eurodatKey = sampleInvoiceableItem.quotationLineCode;
            analysis.executionNumber = sampleInvoiceableItem.executionNumber;
            analysis.subGroupKey = sampleInvoiceableItem.subGroupCode;
            analysis.packageCode = sampleInvoiceableItem.packageCode;
            analysis.structuredComment = sampleInvoiceableItem.comment;
            analysis.isCustomerPackage = false;
            const subGroup = this.currentOrder.quotedItems.quotationCommercialItem.commercialItemSubGroups
                .filter(sg => sg.code === sampleInvoiceableItem.subGroupCode).shift();
            analysis.subgroupCode = subGroup?.comlimsCode;
            if (sampleInvoiceableItem.packageCode) {
                analysis.analysisType = AnalysisType.SubgroupWithPackage;
                const sgPackage = subGroup.packageQuotationLines
                    .filter(pql => pql.code === sampleInvoiceableItem.quotationLineCode)[0];
                if (sgPackage) {
                    analysis.displayName = sgPackage.text;
                    analysis.price = sgPackage.price;
                    analysis.structuredComment = sampleInvoiceableItem.comment;
                    analysis.sortIndex = sgPackage.sortIndex;
                }
            } else if (sampleInvoiceableItem.testCode) {
                let sgTest = subGroup.testQuotationLines
                    .filter(tql => tql.code === sampleInvoiceableItem.quotationLineCode)[0];
                if (!sgTest) {
                    sgTest = this.findMRT(sampleInvoiceableItem.quotationLineCode);
                    analysis.isMandatory = true;
                }
                if (sgTest) {
                    analysis.displayName = sgTest.text;
                    analysis.sortIndex = sgTest.sortIndex;
                    analysis.testCode = sampleInvoiceableItem.testCode;
                    analysis.analysisType = AnalysisType.SubgroupWithTest;
                    analysis.structuredComment = sampleInvoiceableItem.comment;
                    analysis.price = sgTest.price;
                    analysis.isMandatory = sampleInvoiceableItem.isMrt;
                    if (sgTest.preparationTestCodes && sgTest.preparationTestCodes.length > 0) {
                        analysis.preparationTestCodes = sgTest.preparationTestCodes;
                    }
                }
            } else {
                analysis.analysisType = AnalysisType.FullSubgroup;
            }
            return analysis;
        }

        if (isMonoGraphInvoiceableItem) {
            if (!this.quotedItemApplicabilityRule.monoGraphSalesCatalogItems
                || !this.quotedItemApplicabilityRule.isMonoGraphSCApplicable(sampleInvoiceableItem.salesCatagloueCode)?.isValid) {
                if (isPendingOrder) {
                    if (sampleInvoiceableItem.packageCode) {
                        if (!this.deletedPackages?.some(dp => dp.salesCatalogCode === sampleInvoiceableItem.salesCatagloueCode
                            && dp.packageCode === sampleInvoiceableItem.packageCode)) {
                            this.orderCommandService.removeSampleInvoiceableItemForSCPackage(
                                sample.sampleCode, sampleInvoiceableItem.salesCatagloueCode, sampleInvoiceableItem.packageCode).toPromise();
                            this.deletedPackages.push({
                                sequence: sampleInvoiceableItem.id,
                                salesCatalogCode: sampleInvoiceableItem.salesCatagloueCode,
                                packageCode: sampleInvoiceableItem.packageCode
                            });
                        }
                    } else if (sampleInvoiceableItem.testCode) {
                        this.orderCommandService.removeSampleInvoiceableItemForSCTest(
                            sample.sampleCode, sampleInvoiceableItem.salesCatagloueCode, sampleInvoiceableItem.testCode).toPromise();
                    }

                }
                return;
            }
            if (sampleInvoiceableItem.packageCode) {
                const validateResp = this.quotedItemApplicabilityRule.isMonographPackageApplicable(sampleInvoiceableItem.packageCode,
                    sampleInvoiceableItem.monographCode, sampleInvoiceableItem.compendiumCode);
                if (!validateResp.isValid) {
                    if (isPendingOrder) {
                        if (!this.deletedPackages.some(dp => dp.salesCatalogCode === sampleInvoiceableItem.salesCatagloueCode
                            && dp.packageCode === sampleInvoiceableItem.packageCode
                            && dp.monographCode === sampleInvoiceableItem.monographCode)) {
                            this.orderCommandService.removeSampleInvoiceableItemForSCPackage(
                                sample.sampleCode, sampleInvoiceableItem.salesCatagloueCode, sampleInvoiceableItem.packageCode).toPromise();
                            this.deletedPackages.push({
                                sequence: sampleInvoiceableItem.id,
                                salesCatalogCode: sampleInvoiceableItem.salesCatagloueCode,
                                packageCode: sampleInvoiceableItem.packageCode,
                                monographCode: sampleInvoiceableItem.monographCode
                            });
                        }
                    }
                    return;
                }
                const freePackage = validateResp.data;
                if (freePackage) {
                    analysis.analysisType = AnalysisType.MonographPackage;
                    analysis.packageCode = sampleInvoiceableItem.packageCode;
                    analysis.displayName = freePackage.name;
                    analysis.price = freePackage.price;
                    analysis.sortIndex = freePackage.sortIndex;
                    analysis.testCode = null;
                    analysis.compendiumName = freePackage.compendiumName;
                    analysis.monographName = freePackage.monographName;
                    analysis.compendiumCode = freePackage.compendiumCode;
                    analysis.monographCode = freePackage.monographCode;
                    analysis.salesCatalogCode = freePackage.salesCatalogCode;
                    analysis.structuredComment = sampleInvoiceableItem.comment;
                    analysis.executionNumber = sampleInvoiceableItem.executionNumber;
                }
            } else {
                const validateResp = this.quotedItemApplicabilityRule
                    .isMonographTestApplicable(sampleInvoiceableItem.testCode,
                        sampleInvoiceableItem.monographCode, sampleInvoiceableItem.compendiumCode);
                if (!validateResp.isValid) {
                    if (isPendingOrder) {
                        this.orderCommandService.removeSampleInvoiceableItemForSCTest(
                            sample.sampleCode, sampleInvoiceableItem.salesCatagloueCode, sampleInvoiceableItem.testCode).toPromise();
                    }
                    return;
                }
                const test: MonographValidationResponse = validateResp.data;
                if (test) {
                    analysis.analysisType = AnalysisType.MonographTest;
                    analysis.testCode = sampleInvoiceableItem.testCode;
                    analysis.displayName = test.name;
                    analysis.price = test.price;
                    analysis.packageCode = null;
                    analysis.sortIndex = test.sortIndex;
                    analysis.compendiumName = test.compendiumName;
                    analysis.monographName = test.monographName;
                    analysis.compendiumCode = test.compendiumCode;
                    analysis.monographCode = test.monographCode;
                    analysis.structuredComment = sampleInvoiceableItem.comment;
                    if (test.preparationTests && test.preparationTests.length > 0) {
                        analysis.preparationTestCodes = test.preparationTests;
                    }
                    analysis.salesCatalogCode = test.salesCatalogCode;
                    analysis.structuredComment = sampleInvoiceableItem.comment;
                    analysis.executionNumber = sampleInvoiceableItem.executionNumber;
                }
            }
        } else {
            if (sampleInvoiceableItem.packageCode) {
                const validateResp = this.quotedItemApplicabilityRule.isFreePackageApplicable(sampleInvoiceableItem.packageCode);
                if (validateResp.isValid) {
                    const freePackage = validateResp.data;
                    if (freePackage) {
                        analysis.analysisType = AnalysisType.FreeQLPackage;
                        analysis.eurodatKey = freePackage.code;
                        analysis.packageCode = sampleInvoiceableItem.packageCode;
                        analysis.displayName = freePackage.text;
                        analysis.price = freePackage.price;
                        analysis.isCustomerPackage = false;
                        analysis.sortIndex = freePackage.sortIndex;
                        analysis.structuredComment = sampleInvoiceableItem.comment;
                        analysis.executionNumber = sampleInvoiceableItem.executionNumber;
                    }
                }
            } else {
                let mandatoryTest: any;
                const validateResp = this.quotedItemApplicabilityRule
                    .isFreeTestApplicable(sampleInvoiceableItem.testCode, sampleInvoiceableItem.quotationLineCode);
                if (validateResp.isValid) {
                    mandatoryTest = this.findMRT(sampleInvoiceableItem.quotationLineCode);
                    let quotableItemApplicability = true;
                    if (this.currentOrder.orderTemplate?.isSampleLevelMatrix && mandatoryTest) {
                        quotableItemApplicability = this.quotedItemApplicabilityRule
                            .isFreeTestApplicable(mandatoryTest.testCode, mandatoryTest.code)?.isValid
                            || this.quotedItemApplicabilityRule.isSGTestApplicable(null, mandatoryTest.code)?.isValid;
                    }

                    const test = validateResp.data || mandatoryTest;
                    if (test) {
                        analysis.analysisType = AnalysisType.FreeQLTest;
                        analysis.eurodatKey = test.code;
                        analysis.testCode = sampleInvoiceableItem.testCode;
                        analysis.displayName = test.text;
                        analysis.preparationTestCodes = test.preparationTestCodes;
                        analysis.price = test.price;
                        analysis.sortIndex = test.sortIndex;
                        analysis.isCustomerPackage = false;
                        analysis.structuredComment = sampleInvoiceableItem.comment;
                        analysis.isMandatory = sampleInvoiceableItem.isMrt;
                        analysis.executionNumber = sampleInvoiceableItem.executionNumber;
                    }
                }
            }
        }

        return analysis;
    }

    private async commercialItemToAnalysis(
        sampleInvoiceableItem: SampleInvoiceableItems): Promise<Analysis> {

        const analysis: Analysis = {
            sequence: sampleInvoiceableItem.id,
            salesCatalogCode: sampleInvoiceableItem.salesCatagloueCode,
            isCustomerPackage: false,
            structuredComment: sampleInvoiceableItem.comment,
        };

        let validateResp: CommercialItemValidationResponse;
        switch (GetScInvoiceableItemType(sampleInvoiceableItem)) {
            case AnalysisType.SalesCatalogueTest:
                validateResp = this.salesCatalogueCommercialItemRule
                    .validateSalesCatalogueTest(sampleInvoiceableItem.testCode, sampleInvoiceableItem.salesCatagloueCode);
                analysis.analysisType = AnalysisType.SalesCatalogueTest;
                analysis.testCode = sampleInvoiceableItem.testCode;
                analysis.preparationTestCodes = sampleInvoiceableItem.preparationTestCodes;
                break;

            case AnalysisType.SalesCataloguePackage:
                validateResp = this.salesCatalogueCommercialItemRule
                    .validateSalesCataloguePackageCode(sampleInvoiceableItem.packageCode, sampleInvoiceableItem.salesCatagloueCode);
                analysis.analysisType = AnalysisType.SalesCataloguePackage;
                analysis.packageCode = sampleInvoiceableItem.packageCode;
                analysis.testCode = sampleInvoiceableItem.testCode;
                analysis.structuredComment = sampleInvoiceableItem.comment;
                break;

            case AnalysisType.SalesCatalogueTestBundle:
                validateResp = this.salesCatalogueCommercialItemRule
                    .validateSalesCatalogueTestBundle(sampleInvoiceableItem.testBundleCode,
                        sampleInvoiceableItem.testCode, sampleInvoiceableItem.salesCatagloueCode);
                analysis.analysisType = AnalysisType.SalesCatalogueTestBundle;
                analysis.testCode = sampleInvoiceableItem.testCode;
                analysis.testBundleCode = sampleInvoiceableItem.testBundleCode;
                analysis.structuredComment = sampleInvoiceableItem.comment;
                analysis.testBundleDisplayName = validateResp.data.testBundleDisplayName;
                break;
        }

        analysis.price = validateResp.data.price;
        analysis.displayName = validateResp.data.name;
        analysis.isMandatory = sampleInvoiceableItem.isMrt;
        analysis.executionNumber = sampleInvoiceableItem.executionNumber;
        return analysis;
    }

    findMRT(quotationLineCode: string): any {
        let testQuotationLine: TestQuotationLine;

        for (const sg of this.currentOrder.quotedItems.quotationCommercialItem.commercialItemSubGroups) {
            testQuotationLine = sg.testQuotationLines.filter(tql => tql.code === quotationLineCode).shift();
            if (testQuotationLine !== undefined) {
                return testQuotationLine;
            }
        }

        testQuotationLine = this.currentOrder.quotedItems.quotationCommercialItem.freeQuotationLinesTest
            .filter(tql => tql.code === quotationLineCode).shift();
        if (testQuotationLine !== undefined) {
            return testQuotationLine;
        }
    }

    private isUniqueAnalyses(analysis: Analysis, analyses: Analysis[]) {
        if (analysis.analysisType === AnalysisType.CustomerPackage &&
            analyses
                .filter(x => x.analysisType === AnalysisType.CustomerPackage && x.eurodatKey === analysis.eurodatKey).length === 0) {
            return true;
        } else if (analysis.analysisType === AnalysisType.SubgroupWithPackage &&
            analyses
                .filter(x => x.analysisType === AnalysisType.SubgroupWithPackage && x.eurodatKey === analysis.eurodatKey).length === 0) {
            return true;
        } else if (analysis.analysisType === AnalysisType.FreeQLPackage &&
            analyses
                .filter(x => x.analysisType === AnalysisType.FreeQLPackage && x.eurodatKey === analysis.eurodatKey).length === 0) {
            return true;
        } else if (analysis.analysisType === AnalysisType.FreeQLTest || analysis.analysisType === AnalysisType.SubgroupWithTest) {
            return true;
        } else if (analysis.analysisType === AnalysisType.SalesCatalogueTest &&
            !analyses
                .some(x => x.analysisType === AnalysisType.SalesCatalogueTest && x.testCode === analysis.testCode)) {
            return true;
        } else if (analysis.analysisType === AnalysisType.SalesCataloguePackage &&
            !analyses
                .some(x => x.analysisType === AnalysisType.SalesCataloguePackage && x.packageCode === analysis.packageCode)) {
            return true;
        } else if (analysis.analysisType === AnalysisType.SalesCatalogueTestBundle &&
            !analyses.some(x => x.analysisType === AnalysisType.SalesCatalogueTestBundle &&
                x.testBundleCode === analysis.testBundleCode && x.testCode === analysis.testCode)) {
            return true;
        } else if (analysis.analysisType === AnalysisType.MonographTest && analyses
            .filter(x => x.analysisType === AnalysisType.MonographTest && x.testCode === analysis.testCode).length === 0) {
            return true;
        } else if (analysis.analysisType === AnalysisType.MonographPackage && analyses
            .filter(x => x.analysisType === AnalysisType.MonographPackage && x.packageCode === analysis.packageCode).length === 0) {
            return true;
        }
        return false;
    }
}

